import React, { useEffect, useState } from 'react';
import type { Campaign, Account, VoteOption } from '../../store/appStore';
import { useAppStore } from '../../store/appStore';

interface Props {
  campaign: Campaign;
  account: Account;
  isQR: boolean;
}

export const VoteForm: React.FC<Props> = ({ campaign, account, isQR }) => {
  const { fetchVoteOptions, addSuggestion } = useAppStore();
  const [options, setOptions] = useState<VoteOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [voted, setVoted] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVoteOptions(campaign.id)
      .then(setOptions)
      .catch(() => setError('Kunne ikke laste avstemningsalternativer'))
      .finally(() => setIsLoading(false));
  }, [campaign.id]);

  const handleVote = async (label: string) => {
    if (isSubmitting || voted) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await addSuggestion(account.id, label, null, isQR ? 'qr' : 'direct', campaign.id);
      setVoted(label);
    } catch {
      setError('Kunne ikke registrere stemme. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <p style={{ color: '#666666', fontSize: '1rem', fontWeight: 400 }}>Laster...</p>;
  }

  if (voted) {
    return (
      <div className="text-center py-12">
        <div className="p-4 sm:p-6 mb-6" style={{ backgroundColor: '#ffffff', border: '2px solid #000000' }}>
          <h3 style={{ color: '#000000', fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Stemme registrert!</h3>
          <p style={{ color: '#222222', fontSize: '1rem', fontWeight: 400 }}>
            Du stemte: <strong style={{ fontWeight: 600 }}>{voted}</strong>
          </p>
        </div>
        <p style={{ color: '#666666', fontSize: '0.875rem', fontWeight: 400 }}>Takk for at du deltar i demokratiet.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 style={{ color: '#000000', fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: '0.5rem' }}>
          {campaign.title}
        </h1>
        {campaign.description && (
          <p style={{ color: '#666666', fontSize: '1.125rem', fontWeight: 300, lineHeight: 1.7 }}>
            {campaign.description}
          </p>
        )}
      </div>

      {error && (
        <div className="mb-4" style={{ backgroundColor: '#ffffff', border: '2px solid #ef6f64', padding: '0.75rem 1rem' }}>
          <p style={{ color: '#ef6f64', fontSize: '0.875rem', fontWeight: 500 }}>{error}</p>
        </div>
      )}

      <p
        className="uppercase mb-4"
        style={{ color: '#000000', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em' }}
      >
        Velg ditt alternativ
      </p>

      <div className="space-y-3">
        {options.map(option => (
          <button
            key={option.id}
            onClick={() => handleVote(option.label)}
            disabled={isSubmitting}
            className="w-full text-left transition-colors"
            style={{
              border: '2px solid #000000',
              borderRadius: 0,
              backgroundColor: '#ffffff',
              color: '#000000',
              fontSize: '1rem',
              fontWeight: 700,
              padding: '1rem 1.25rem',
              opacity: isSubmitting ? 0.5 : 1,
            }}
            onMouseOver={e => {
              e.currentTarget.style.backgroundColor = '#000000';
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseOut={e => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = '#000000';
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {options.length === 0 && (
        <p style={{ color: '#666666', fontSize: '0.875rem', fontWeight: 400 }}>Ingen alternativer er lagt til ennå.</p>
      )}

      <p style={{ textAlign: 'center', color: '#999999', fontSize: '0.65rem', marginTop: '12px' }}>
        Ved å stemme godtar du vår <a href="/personvern" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>personvernerklæring</a>
      </p>
    </div>
  );
};
